// Shared data for Orrbbit Business marketing pages (marketing-only; the mobile
// app + Business backend remain the source of truth for real functionality).
import {
  Users, CalendarDays, BriefcaseBusiness, Store, BadgeCheck, CreditCard,
  ClipboardList, ShieldCheck, Star, LayoutDashboard, Sparkles, MapPin,
} from "lucide-react";

export const BUSINESS_PRICE = "A$9.99/month (local store equivalent outside Australia)";

export const businessHowItWorks = [
  { title: "Create your Business account", body: "Sign up as a Business to get started." },
  { title: "Complete Business details", body: "Add your name, category, location and description." },
  { title: "Complete verification", body: "Provide the details and documents Orrbbit needs to review." },
  { title: "Review your plan", body: "After verification, eligible subscribers get seven days free, then A$9.99/month (local store equivalent outside Australia). Subscribe in the mobile app; access syncs to the web." },
  { title: "Verification Pending", body: "Your submission enters Orrbbit's review queue." },
  { title: "Orrbbit reviews the Business", body: "Our team reviews your Business against our standards." },
  { title: "Verified Business unlocks", body: "Once verified, an active Business trial or subscription unlocks the full Business tools." },
  { title: "Start hosting Business Events", body: "Create events your local community can discover nearby." },
];

export const businessPricingFeatures = [
  "Business Profile",
  "Local deals and offers",
  "QR and manual-code redemption",
  "Verified Business badge once approved",
  "Business Hosted Events",
  "Nearby Event discovery",
  "Event poster/photo uploads",
  "Attendee management",
  "Mobile Business Dashboard",
  "Desktop Business Dashboard",
  "Business insights",
  "Event ratings and reviews",
];

export const businessCategories = [
  "Networking", "Business Networking", "Coffee / Drinks", "Food", "Food Deals / Discounts",
  "Happy Hour", "Fitness", "Sport", "Community", "Live Music", "Entertainment", "Workshop",
  "Classes", "Wellness", "Hospitality", "Launch Event", "Market", "Promotion",
  "Networking Meetup", "Other",
];

// The Business story told in real-world photos (marketing illustration).
export const businessStory = [
  { slug: "cafe-owner-woman", caption: "A local business joins Orrbbit" },
  { slug: "barista-pouring", caption: "They host a real experience" },
  { slug: "outdoor-gym", caption: "Nearby people discover it on their Radar" },
  { slug: "lobby-networking", caption: "People physically attend" },
  { slug: "women-chatting-four", caption: "Local relationships grow" },
];

// Realistic business-hosted event examples (illustrative).
export const businessEventExamples = [
  { title: "Local Networking Night", tag: "Networking", when: "Friday · 6:00 PM", distance: "350m away" },
  { title: "Friday Food Special", tag: "Food Deals", when: "5:00 PM–8:00 PM · 20% off selected meals", distance: "420m away" },
  { title: "Community Fitness Session", tag: "Fitness", when: "Saturday · 8:00 AM", distance: "650m away" },
];

export const businessHighlights = [
  { icon: BadgeCheck, text: "Verified Business Profile" },
  { icon: CalendarDays, text: "Host Business Events" },
  { icon: MapPin, text: "Appear in nearby Event discovery" },
  { icon: Users, text: "Manage attendees" },
  { icon: LayoutDashboard, text: "Desktop + mobile management" },
  { icon: Sparkles, text: "Business insights" },
  { icon: Star, text: "Event ratings and reviews" },
  { icon: Store, text: "Local community visibility" },
];

export const businessTrust = [
  { icon: ShieldCheck, title: "Verified Businesses", body: "Businesses complete a verification process before verified Business features are unlocked." },
  { icon: Star, title: "Confirmed Event Reviews", body: "Business Event reviews come from eligible attendees after the Event has ended." },
  { icon: MapPin, title: "Local-first", body: "Business Events are discovered based on location, relevance and user filters." },
];

// Three main experiences.
export const pillars = [
  {
    to: "/people", icon: Users, tone: "teal", title: "People", photo: "friends-coffee-shop",
    body: "Meet people around you based on your vibe and what you're open to.",
    chips: ["People Radar", "Vibes", "Nearby", "Pings", "Encounters", "Personal & Business Events"],
  },
  {
    to: "/professional", icon: BriefcaseBusiness, tone: "orange", title: "Professional", photo: "mentor-window",
    body: "Find verified people nearby who can help — or offer your help to others.",
    chips: ["Need Help", "Can Help", "Verified Professionals", "Available Now", "Requests"],
  },
  {
    to: "/business", icon: Store, tone: "cobalt", title: "Business", photo: "cafe-owners",
    body: "Create a verified local presence and host experiences your community can discover nearby.",
    chips: ["Verified Business Profile", "Business Hosted Events", "Attendee management", "Reviews", "Analytics", "Desktop dashboard"],
  },
];

export const iconMap = { Users, CalendarDays, BriefcaseBusiness, Store, BadgeCheck, CreditCard, ClipboardList, ShieldCheck, Star, LayoutDashboard, Sparkles, MapPin };

